/**
 * Internationalization configuration
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      // Common
      common: {
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        add: 'Add',
        update: 'Update',
        submit: 'Submit',
        close: 'Close',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        farms: 'My Farms',
        mrv: 'MRV Reports',
        credits: 'Carbon Credits',
        marketplace: 'Marketplace',
        profile: 'Profile',
        notifications: 'Notifications',
        help: 'Help',
        logout: 'Logout',
      },
      
      // Auth
      auth: {
        login: 'Login',
        register: 'Register',
        email: 'Email',
        phone: 'Phone Number',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot Password?',
        loginSuccess: 'Logged in successfully',
        registerSuccess: 'Registration successful',
        logoutSuccess: 'Logged out successfully',
        invalidCredentials: 'Invalid credentials',
        emailRequired: 'Email is required',
        phoneRequired: 'Phone number is required',
        passwordRequired: 'Password is required',
        createAccount: 'Create Account',
        alreadyHaveAccount: 'Already have an account?',
        dontHaveAccount: "Don't have an account?",
      },
      
      // Profile
      profile: {
        firstName: 'First Name',
        lastName: 'Last Name',
        gender: 'Gender',
        village: 'Village',
        block: 'Block',
        district: 'District',
        state: 'State',
        pincode: 'PIN Code',
        tribalGroup: 'Tribal Group',
        shgMembership: 'SHG Membership',
        farmingExperience: 'Farming Experience (years)',
        totalLandArea: 'Total Land Area (hectares)',
        irrigationAccess: 'Irrigation Access',
        hasSmartphone: 'Has Smartphone',
        whatsappNumber: 'WhatsApp Number',
        preferredContact: 'Preferred Contact Method',
        updateProfile: 'Update Profile',
        profileUpdated: 'Profile updated successfully',
        // Additional fields
        phoneNumber: 'Phone Number',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        createAccount: 'Create Account',
        joinClimateSolution: 'Join the climate solution platform for farmers',
        alreadyHaveAccount: 'Already have an account?',
        login: 'Login',
        dontHaveAccount: "Don't have an account?",
        register: 'Register',
        passwordHint: 'Minimum 8 characters with uppercase, lowercase, number and symbol',
        confirmPasswordHint: 'Confirm your password',
        welcomeBack: 'Welcome back to your MRV dashboard',
        emailOrPhone: 'Email or Phone Number',
        emailOrPhonePlaceholder: 'farmer@example.com or 9876543210',
        passwordPlaceholder: 'Enter your password',
        passwordRequired: 'Password is required',
      },
      
      // Farms
      farms: {
        myFarms: 'My Farms',
        addFarm: 'Add New Farm',
        farmName: 'Farm Name',
        farmArea: 'Farm Area (hectares)',
        irrigationType: 'Irrigation Type',
        waterSource: 'Water Source',
        soilType: 'Soil Type',
        surveyNumber: 'Survey Number',
        elevation: 'Elevation (meters)',
        coordinates: 'Coordinates',
        farmAdded: 'Farm added successfully',
        farmUpdated: 'Farm updated successfully',
        farmDeleted: 'Farm deleted successfully',
        noFarms: 'No farms found',
        selectLocation: 'Select farm location on map',
      },
      
      // MRV
      mrv: {
        reports: 'MRV Reports',
        newReport: 'New MRV Report',
        reportType: 'Report Type',
        methodology: 'Methodology',
        baselineEmissions: 'Baseline Emissions',
        projectEmissions: 'Project Emissions',
        emissionReductions: 'Emission Reductions',
        verificationStatus: 'Verification Status',
        verified: 'Verified',
        pending: 'Pending',
        rejected: 'Rejected',
        draft: 'Draft',
        submitted: 'Submitted',
        underReview: 'Under Review',
        published: 'Published',
        uncertaintyRange: 'Uncertainty Range',
        qualityAssurance: 'Quality Assurance',
        satelliteData: 'Satellite Data',
        farmerData: 'Farmer Data',
        processReport: 'Process MRV Report',
        reportProcessed: 'MRV report processed successfully',
      },
      
      // Carbon Credits
      credits: {
        carbonCredits: 'Carbon Credits',
        totalCredits: 'Total Credits',
        availableCredits: 'Available Credits',
        soldCredits: 'Sold Credits',
        retiredCredits: 'Retired Credits',
        creditQuantity: 'Quantity (tCO2e)',
        methodology: 'Methodology',
        vintage: 'Vintage Year',
        serialNumber: 'Serial Number',
        status: 'Status',
        issued: 'Issued',
        listed: 'Listed',
        sold: 'Sold',
        retired: 'Retired',
        cancelled: 'Cancelled',
        mintCredit: 'Mint Credit',
        listCredit: 'List for Sale',
        retireCredit: 'Retire Credit',
        creditMinted: 'Carbon credit minted successfully',
        creditListed: 'Carbon credit listed for sale',
        creditRetired: 'Carbon credit retired',
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Welcome back',
        overview: 'Overview',
        totalFarms: 'Total Farms',
        totalCredits: 'Total Credits',
        totalEmissionReductions: 'Total Emission Reductions',
        totalIncome: 'Total Income',
        recentActivity: 'Recent Activity',
        pendingReports: 'Pending Reports',
        seasonalProgress: 'Seasonal Progress',
        emissionTrends: 'Emission Trends',
        farmingMethod: 'Farming Method',
        conventional: 'Conventional',
        awd: 'Alternate Wetting & Drying',
        sri: 'System of Rice Intensification',
        organic: 'Organic',
        integrated: 'Integrated',
      },
      
      // Seasons
      seasons: {
        kharif: 'Kharif',
        rabi: 'Rabi',
        zaid: 'Zaid',
        season: 'Season',
        year: 'Year',
        crop: 'Crop',
        variety: 'Variety',
        sowingDate: 'Sowing Date',
        transplantDate: 'Transplanting Date',
        harvestDate: 'Harvest Date',
        farmingMethod: 'Farming Method',
        expectedYield: 'Expected Yield',
        actualYield: 'Actual Yield',
        yieldUnit: 'Yield Unit',
        irrigationCycles: 'Irrigation Cycles',
        organicInputs: 'Organic Inputs',
        fertilizers: 'Fertilizers',
        pesticides: 'Pesticides',
      },
      
      // Notifications
      notifications: {
        title: 'Notifications',
        markAllRead: 'Mark All as Read',
        noNotifications: 'No notifications',
        irrigationReminder: 'Irrigation Reminder',
        dataCollection: 'Data Collection',
        verificationResult: 'Verification Result',
        creditIssued: 'Credit Issued',
        paymentReceived: 'Payment Received',
        systemUpdate: 'System Update',
        emergency: 'Emergency',
      },
      
      // Errors
      errors: {
        generic: 'Something went wrong. Please try again.',
        network: 'Network error. Please check your connection.',
        unauthorized: 'You are not authorized to perform this action.',
        forbidden: 'Access denied.',
        notFound: 'The requested resource was not found.',
        validation: 'Please check your input and try again.',
        server: 'Server error. Please try again later.',
      },
    },
  },
  
  hi: {
    translation: {
      // Common
      common: {
        loading: 'लोड हो रहा है...',
        save: 'सेव करें',
        cancel: 'रद्द करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        view: 'देखें',
        add: 'जोड़ें',
        update: 'अपडेट करें',
        submit: 'जमा करें',
        close: 'बंद करें',
        confirm: 'पुष्टि करें',
        yes: 'हां',
        no: 'नहीं',
        error: 'त्रुटि',
        success: 'सफलता',
        warning: 'चेतावनी',
        info: 'जानकारी',
      },
      
      // Navigation
      nav: {
        dashboard: 'डैशबोर्ड',
        farms: 'मेरे खेत',
        mrv: 'MRV रिपोर्ट',
        credits: 'कार्बन क्रेडिट',
        marketplace: 'बाज़ार',
        profile: 'प्रोफ़ाइल',
        notifications: 'सूचनाएं',
        help: 'सहायता',
        logout: 'लॉगआउट',
      },
      
      // Auth
      auth: {
        login: 'लॉगिन',
        register: 'पंजीकरण',
        email: 'ईमेल',
        phone: 'फोन नंबर',
        password: 'पासवर्ड',
        confirmPassword: 'पासवर्ड की पुष्टि करें',
        forgotPassword: 'पासवर्ड भूल गए?',
        loginSuccess: 'सफलतापूर्वक लॉगिन हो गए',
        registerSuccess: 'पंजीकरण सफल',
        logoutSuccess: 'सफलतापूर्वक लॉगआउट हो गए',
        invalidCredentials: 'गलत क्रेडेंशियल',
        emailRequired: 'ईमेल आवश्यक है',
        phoneRequired: 'फोन नंबर आवश्यक है',
        passwordRequired: 'पासवर्ड आवश्यक है',
        createAccount: 'खाता बनाएं',
        alreadyHaveAccount: 'पहले से खाता है?',
        dontHaveAccount: 'खाता नहीं है?',
      },
      
      // Profile
      profile: {
        firstName: 'पहला नाम',
        lastName: 'अंतिम नाम',
        gender: 'लिंग',
        village: 'गांव',
        block: 'ब्लॉक',
        district: 'जिला',
        state: 'राज्य',
        pincode: 'पिन कोड',
        tribalGroup: 'आदिवासी समुदाय',
        shgMembership: 'SHG सदस्यता',
        farmingExperience: 'खेती का अनुभव (वर्ष)',
        totalLandArea: 'कुल भूमि क्षेत्र (हेक्टेयर)',
        irrigationAccess: 'सिंचाई की सुविधा',
        hasSmartphone: 'स्मार्टफोन है',
        whatsappNumber: 'व्हाट्सएप नंबर',
        preferredContact: 'पसंदीदा संपर्क तरीका',
        updateProfile: 'प्रोफ़ाइल अपडेट करें',
        profileUpdated: 'प्रोफ़ाइल सफलतापूर्वक अपडेट हुई',
        // Additional fields
        phoneNumber: 'फोन नंबर',
        email: 'ईमेल',
        password: 'पासवर्ड',
        confirmPassword: 'पासवर्ड की पुष्टि करें',
        createAccount: 'खाता बनाएं',
        joinClimateSolution: 'किसानों के लिए जलवायु समाधान प्लेटफॉर्म में शामिल हों',
        alreadyHaveAccount: 'पहले से खाता है?',
        login: 'लॉगिन',
        dontHaveAccount: 'खाता नहीं है?',
        register: 'पंजीकरण',
        passwordHint: 'न्यूनतम 8 अक्षर, बड़े अक्षर, छोटे अक्षर, संख्या और प्रतीक के साथ',
        confirmPasswordHint: 'अपना पासवर्ड पुष्टि करें',
        welcomeBack: 'अपने MRV डैशबोर्ड में वापस आपका स्वागत है',
        emailOrPhone: 'ईमेल या फोन नंबर',
        emailOrPhonePlaceholder: 'farmer@example.com या 9876543210',
        passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
        passwordRequired: 'पासवर्ड आवश्यक है',
      },
      
      // Farms
      farms: {
        myFarms: 'मेरे खेत',
        addFarm: 'नया खेत जोड़ें',
        farmName: 'खेत का नाम',
        farmArea: 'खेत का क्षेत्रफल (हेक्टेयर)',
        irrigationType: 'सिंचाई का प्रकार',
        waterSource: 'पानी का स्रोत',
        soilType: 'मिट्टी का प्रकार',
        surveyNumber: 'सर्वे नंबर',
        elevation: 'ऊंचाई (मीटर)',
        coordinates: 'निर्देशांक',
        farmAdded: 'खेत सफलतापूर्वक जोड़ा गया',
        farmUpdated: 'खेत सफलतापूर्वक अपडेट हुआ',
        farmDeleted: 'खेत सफलतापूर्वक हटाया गया',
        noFarms: 'कोई खेत नहीं मिला',
        selectLocation: 'मानचित्र पर खेत की स्थिति चुनें',
      },
      
      // Dashboard
      dashboard: {
        welcome: 'वापसी पर स्वागत है',
        overview: 'अवलोकन',
        totalFarms: 'कुल खेत',
        totalCredits: 'कुल क्रेडिट',
        totalEmissionReductions: 'कुल उत्सर्जन कमी',
        totalIncome: 'कुल आय',
        recentActivity: 'हाल की गतिविधि',
        pendingReports: 'लंबित रिपोर्ट',
        seasonalProgress: 'मौसमी प्रगति',
        emissionTrends: 'उत्सर्जन रुझान',
        farmingMethod: 'खेती की पद्धति',
        conventional: 'पारंपरिक',
        awd: 'वैकल्पिक गीला और सूखा',
        sri: 'चावल गहनता प्रणाली',
        organic: 'जैविक',
        integrated: 'एकीकृत',
      },
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || 'hi', // Default to Hindi
    fallbackLng: 'en',
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: false,
    },
  });

export default i18n;
